import { z } from 'zod';
import { LOCALES } from './i18n';

/**
 * "Ideen & Feedback": families tell us what is broken, what could be better and what is missing.
 * One schema for the form and the callable `sendFeedback`.
 */
export const FEEDBACK_KINDS = ['bug', 'improvement', 'feature'] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

export const FEEDBACK_MESSAGE_MAX = 4000;

export const FeedbackInput = z
  .object({
    kind: z.enum(FEEDBACK_KINDS),
    rating: z.number().int().min(1).max(5).nullish(),
    message: z.string().trim().max(FEEDBACK_MESSAGE_MAX),
    /** Two separate, voluntary consents – never pre-ticked. */
    contact: z.boolean(),
    quote: z.boolean(),
    /** Language of the form – which consent text was shown (1.19); older apps send none. */
    locale: z.enum(LOCALES).nullish(),
    /** Collected automatically so a bug report is reproducible without asking back. */
    context: z.object({
      view: z.string().max(100),
      version: z.string().max(20),
      device: z.enum(['phone', 'tablet', 'desktop', 'wall']),
      installed: z.boolean(),
    }),
  })
  // a rating alone is feedback too; an empty form is not
  .refine((f) => f.message.length > 0 || f.rating != null, { path: ['message'] });
export type FeedbackInput = z.infer<typeof FeedbackInput>;

/**
 * Consent texts exactly as shown in the form. The server stores the version and a hash of the
 * text with each consent, so we can always prove what someone agreed to (DSGVO, UWG for quotes).
 * Change a text → bump the version. Keys per language: German is the master (release 1.18).
 */
export const FEEDBACK_CONSENT = {
  version: 'v1',
  de: {
    contact: 'Ihr dürft mich dazu per E-Mail kontaktieren.',
    quote: 'Ihr dürft mein Feedback anonymisiert (ohne Namen) als Zitat auf der Website verwenden.',
  },
  en: {
    contact: 'You may contact me about this by email.',
    quote: 'You may use my feedback anonymously (without my name) as a quote on the website.',
  },
} as const;
